/**
 * Parser for extracting and handling references in article content.
 */

export const REFERENCE_PATTERNS = {
  NUMBERED_WITH_SOURCE: /(\d+):(\d+)†((?:[A-Za-z0-9_.-]+\s)*[A-Za-z0-9_.-]+\.(?:txt|json|pdf|doc|docx|xls|xlsx))/g,
  BRACKETED_SOURCE: /\[([^\]]+\.(?:txt|json|pdf|doc|docx|xls|xlsx))\]/g,
  BRACKETED_CLEAN: /\[([a-zA-Z0-9_]+)\]/g,
  NUMBERED_ONLY: /\[(\d+):(\d+)\]/g,
  VECTOR_STORE_EMOJI: /📄\s*(\d+)/g,
  SIMPLE_NUMBERED: /\[(\d+)\]/g,
  VECTOR_STORE_PREFIXED: /\[VS(\d+)\]/g
};

export const extractReferences = (text) => {
  if (!text || typeof text !== 'string') return [];

  const references = [];
  let match;

  const numberedWithSourcePattern = new RegExp(REFERENCE_PATTERNS.NUMBERED_WITH_SOURCE.source, 'g');
  while ((match = numberedWithSourcePattern.exec(text)) !== null) {
    references.push({
      type: 'numbered_with_source',
      original: match[0],
      sourceId: match[1],
      itemId: match[2],
      filename: match[3],
      displayText: `${match[1]}:${match[2]}`,
      position: {
        start: match.index,
        end: match.index + match[0].length
      }
    });
  }

  const bracketedPattern = new RegExp(REFERENCE_PATTERNS.BRACKETED_SOURCE.source, 'g');
  while ((match = bracketedPattern.exec(text)) !== null) {
    references.push({
      type: 'bracketed_source',
      original: match[0],
      filename: match[1],
      displayText: match[1],
      position: {
        start: match.index,
        end: match.index + match[0].length
      }
    });
  }

  const bracketedCleanPattern = new RegExp(REFERENCE_PATTERNS.BRACKETED_CLEAN.source, 'g');
  while ((match = bracketedCleanPattern.exec(text)) !== null) {
    const token = String(match[1] || '').trim();
    const looksLikeSourceToken = /\b(source|vector|dataset|document|doc|file|plik|pdf|txt|json)\b/i.test(token);
    if (!looksLikeSourceToken) {
      continue;
    }

    references.push({
      type: 'bracketed_clean',
      original: match[0],
      filename: `${token}.txt`,
      cleanName: token,
      displayText: token,
      position: {
        start: match.index,
        end: match.index + match[0].length
      }
    });
  }

  const numberedOnlyPattern = new RegExp(REFERENCE_PATTERNS.NUMBERED_ONLY.source, 'g');
  while ((match = numberedOnlyPattern.exec(text)) !== null) {
    references.push({
      type: 'numbered_only',
      original: match[0],
      sourceId: match[1],
      itemId: match[2],
      displayText: `${match[1]}:${match[2]}`,
      position: {
        start: match.index,
        end: match.index + match[0].length
      }
    });
  }

  const vectorStorePattern = new RegExp(REFERENCE_PATTERNS.VECTOR_STORE_EMOJI.source, 'g');
  while ((match = vectorStorePattern.exec(text)) !== null) {
    references.push({
      type: 'vector_store',
      original: match[0],
      sourceId: match[1],
      filename: `Vector Store Source ${match[1]}`,
      displayText: `Source ${match[1]}`,
      position: {
        start: match.index,
        end: match.index + match[0].length
      }
    });
  }

  const vectorStorePrefixedPattern = new RegExp(REFERENCE_PATTERNS.VECTOR_STORE_PREFIXED.source, 'g');
  while ((match = vectorStorePrefixedPattern.exec(text)) !== null) {
    references.push({
      type: 'vector_store_prefixed',
      original: match[0],
      sourceId: match[1],
      filename: `Vector Store Source ${match[1]}`,
      displayText: `Vector Store ${match[1]}`,
      position: {
        start: match.index,
        end: match.index + match[0].length
      }
    });
  }

  const simpleNumberedPattern = new RegExp(REFERENCE_PATTERNS.SIMPLE_NUMBERED.source, 'g');
  while ((match = simpleNumberedPattern.exec(text)) !== null) {
    const currentMatch = match;
    const existingRef = references.find((ref) =>
      ref.position.start <= currentMatch.index
      && ref.position.end >= currentMatch.index + currentMatch[0].length
    );

    if (!existingRef) {
      references.push({
        type: 'simple_numbered',
        original: currentMatch[0],
        sourceId: currentMatch[1],
        filename: `Source ${currentMatch[1]}`,
        displayText: `Source ${currentMatch[1]}`,
        position: {
          start: currentMatch.index,
          end: currentMatch.index + currentMatch[0].length
        }
      });
    }
  }

  return references.sort((a, b) => a.position.start - b.position.start);
};

export const parseFilename = (filename) => {
  if (!filename) {
    return {
      filename: '',
      extension: '',
      baseName: '',
      date: null,
      isDocument: false,
      isSpreadsheet: false
    };
  }

  const extension = filename.split('.').pop() || '';
  const baseName = filename.replace(/\.[^/.]+$/, '');
  const datePattern = /(\d{8}-\d{5,6})/;
  const dateMatch = filename.match(datePattern);
  const date = dateMatch ? dateMatch[1] : null;

  return {
    filename,
    extension,
    baseName,
    date,
    isDocument: ['txt', 'json', 'pdf', 'doc', 'docx'].includes(extension.toLowerCase()),
    isSpreadsheet: ['xls', 'xlsx', 'csv'].includes(extension.toLowerCase())
  };
};

export const getFileIcon = (filename, type = null) => {
  if (!filename) return '📎';

  if (type === 'vector_store' || type === 'vector_store_prefixed' || filename?.includes('Vector Store')) {
    return '🗂️';
  }

  if (type === 'simple_numbered' && filename?.startsWith('Source ')) {
    return '📚';
  }

  const { extension } = parseFilename(filename);

  switch (extension.toLowerCase()) {
    case 'txt':
      return '📄';
    case 'json':
      return '📋';
    case 'pdf':
      return '📕';
    case 'doc':
    case 'docx':
      return '📘';
    case 'xls':
    case 'xlsx':
    case 'csv':
      return '📊';
    default:
      return '📄';
  }
};
